import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Skeleton } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { AnyAction } from 'redux';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, format, isValid } from 'date-fns';

import { Stores, AppPermissions, conciliationHeaderKeys } from '../types';
import { dateResponseMap, dateRangeRequestMap } from '../helpers';

import {
  changeFilterCountry,
  changeConciliationDetailFilterStore,
  changeConciliationDetailFilterStartAccountingDate,
  changeConciliationDetailFilterEndAccountingDate,
  reportFilterCountryCodeSelector,
  reportConciliationDetailFilterStoresSelector,
  reportFilterStartAccountingDateSelector,
  reportFilterEndAccountingDateSelector,
  findSynchroHistoryRequested,
  synchroLoadingSelector,
  filterParametersLoadedSelector,
  activeCountriesLoadedSelector,
  synchroAuthorizedCountriesSelector,
  synchroLimitDaysSelector,
  synchroEnabledCountriesSelector,
} from '../state';

import DateRangeLimitModal from './Modals/DateRangeLimitModal';

import {
  CountryFilter,
  ReportFilterActions,
  ReportFilters,
  StoreFilter,
  AccountingDateRangeFilter,
} from './ReportFilters';
import { PermissionsProvider } from './Permissions';

import routes from '../config/routes';

const dateFilterConfig = {
  selectorStartDate: reportFilterStartAccountingDateSelector,
  selectorEndDate: reportFilterEndAccountingDateSelector,
  actionStartDate: changeConciliationDetailFilterStartAccountingDate,
  actionEndDate: changeConciliationDetailFilterEndAccountingDate,
};

const handleSearchParams = (dispatch: Dispatch<AnyAction>, searchParams: URLSearchParams) => {
  const countryCode = searchParams.get(conciliationHeaderKeys.country);
  const store = searchParams.get(conciliationHeaderKeys.store);
  const accountingDate = dateResponseMap(
    searchParams.get(conciliationHeaderKeys.accountingDate)
  ) as Date;

  if (accountingDate) {
    dispatch(dateFilterConfig.actionStartDate(accountingDate));
    dispatch(dateFilterConfig.actionEndDate(accountingDate));
  }

  if (store && countryCode) {
    const stores = [{ countryCode, storeAcronym: store }] as Stores[];
    dispatch(changeConciliationDetailFilterStore(stores));
  }
};

const SynchroForMeSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [isReady, setIsReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const countryCode = useSelector(reportFilterCountryCodeSelector);
  const stores = useSelector(reportConciliationDetailFilterStoresSelector);
  const startDate = useSelector(dateFilterConfig.selectorStartDate);
  const endDate = useSelector(dateFilterConfig.selectorEndDate);
  const startDateValue = useSelector(reportFilterStartAccountingDateSelector);
  const endDateValue = useSelector(reportFilterEndAccountingDateSelector);
  const isParamsLoaded = useSelector(filterParametersLoadedSelector);
  const isCountriesLoaded = useSelector(activeCountriesLoadedSelector);
  const synchroReportLimits = useSelector(synchroLimitDaysSelector);
  const authorizedCountries = useSelector(synchroAuthorizedCountriesSelector);
  const enabledCountries = useSelector(synchroEnabledCountriesSelector);

  useEffect(() => {
    if (!isParamsLoaded || !isCountriesLoaded) {
      return undefined;
    }

    const timer = setTimeout(() => setIsReady(true), 400);

    return () => clearTimeout(timer);
  }, [isParamsLoaded, isCountriesLoaded]);

  useEffect(() => {
    if (isReady && !countryCode && authorizedCountries.length > 0) {
      dispatch(changeFilterCountry(authorizedCountries[0].countryCode));
    }
  }, [isReady, countryCode, authorizedCountries, dispatch]);

  useEffect(() => {
    handleSearchParams(dispatch, searchParams);
  }, [dispatch, searchParams]);

  const isCountryLocked = useMemo(() => authorizedCountries.length === 1, [authorizedCountries]);

  const canExecuteSynchro = useMemo(
    () => enabledCountries.includes(countryCode),
    [enabledCountries, countryCode]
  );

  const onedriveAction = () => {
    const fromDt = startDateValue?.accept;
    const toDt = endDateValue?.accept;

    const payload = {
      countryAcronym: countryCode,
      dateFrom: fromDt ? dateRangeRequestMap(fromDt, fromDt)?.split(',')[0] : '',
      dateTo: toDt ? dateRangeRequestMap(toDt, toDt)?.split(',')[0] : '',
      stores: stores.map(s => s.storeAcronym).join(','),
      isSentToOnedrive: true,
    };

    return findSynchroHistoryRequested(payload, true);
  };

  const isTooLarge = useMemo(() => {
    const fromDt = startDate?.accept;
    const toDt = endDate?.accept;
    if (!fromDt || !toDt) return false;
    if (!isValid(fromDt) || !isValid(toDt)) return false;
    const days = differenceInCalendarDays(toDt, fromDt) + 1;
    return days > (synchroReportLimits || 10);
  }, [startDate, endDate, synchroReportLimits]);

  const beforeApply = () => {
    if (!isTooLarge) return true;
    setModalOpen(true);
    return false;
  };

  const applyDisabled = !canExecuteSynchro || !(startDate.accept && endDate.accept && countryCode);

  const applyAction = () => {
    const fromDt = startDateValue?.accept;
    const toDt = endDateValue?.accept;
    const payload = {
      countryAcronym: countryCode,
      dateFrom: fromDt ? dateRangeRequestMap(fromDt, fromDt)?.split(',')[0] : '',
      dateTo: toDt ? dateRangeRequestMap(toDt, toDt)?.split(',')[0] : '',
      stores: stores.map(s => s.storeAcronym).join(','),
      isSentToOnedrive: false,
    };
    return findSynchroHistoryRequested(payload);
  };

  if (!isReady)
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
      </Box>
    );

  return (
    <>
      <DateRangeLimitModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxDays={synchroReportLimits || 10}
      />
      <ReportFilters
        loadingSelector={synchroLoadingSelector}
        applyAction={applyAction}
        onedriveAction={onedriveAction}
      >
        <PermissionsProvider resourcePermissions={[AppPermissions.SYNCHRO_4_ME]}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={2}>
              <CountryFilter fullWidth allowedCountryCodes={enabledCountries} />
            </Grid>
            <Grid item xs={12} md={3}>
              <StoreFilter
                selector={reportConciliationDetailFilterStoresSelector}
                action={changeConciliationDetailFilterStore}
              />
            </Grid>
            <Grid item xs={12} md lg xl>
              <AccountingDateRangeFilter spacing={1} />
            </Grid>
            <Grid
              item
              xs={12}
              md
              lg
              xl
              sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUpload />}
                onClick={() => navigate(routes.protectedNestedRoutes.synchro.children.create.path)}
                disabled={!canExecuteSynchro}
              >
                {t('navigation.buttons.synchro')}
              </Button>
              <ReportFilterActions applyDisabled={applyDisabled} beforeApply={beforeApply} />
            </Grid>
          </Grid>
        </PermissionsProvider>
      </ReportFilters>
    </>
  );
};

export default SynchroForMeSearch;
